import { addDays, format, getISOWeek, isValid, parseISO, startOfISOWeek } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { ItemAvailability, ShopItem } from "./types";

/** How many days an item counts as new for once it goes on sale. */
export const NEW_ITEM_DAYS = 7;

/** How far ahead `getAvailableUntil` looks for the end of a sale. */
const UNTIL_LOOKAHEAD_DAYS = 60;

/**
 * When the weekly drops go on sale (see releases.ts): Thursdays at 5pm,
 * London time, as everyone heads to the pub.
 */
export const WEEKLY_DROP_TIME = "17:00";

const ONE_OFF_DATE = /^\d{4}-\d{2}-\d{2}$/;
const YEARLY_DATE = /^\d{2}-\d{2}$/;
const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

/** The availability of an item in a weekly drop, on sale from 5pm on `day`. */
export function weeklyDrop(day: string): ItemAvailability {
  return { from: day, fromTime: WEEKLY_DROP_TIME };
}

/** The London calendar day (`yyyy-MM-dd`) that `now` falls on. */
export function londonDay(now: Date) {
  return formatInTimeZone(now, "Europe/London", "yyyy-MM-dd");
}

/** The London time of day (`HH:mm`) that `now` falls on. */
function londonTime(now: Date) {
  return formatInTimeZone(now, "Europe/London", "HH:mm");
}

/** Steps a `yyyy-MM-dd` day by whole calendar days. */
function shiftDay(day: string, days: number) {
  return format(addDays(parseISO(day), days), "yyyy-MM-dd");
}

/**
 * Explains what's wrong with an availability, or returns undefined when it's
 * valid. Used by items.test.ts to keep the catalogue honest.
 */
export function availabilityProblem(availability: ItemAvailability): string | undefined {
  const { from, to, weeks, weeksOf, fromTime } = availability;
  const dates = [from, to].filter((date) => date !== undefined);

  if (fromTime !== undefined && !(from && ONE_OFF_DATE.test(from) && TIME.test(fromTime))) {
    return "fromTime needs a one-off from date and an HH:mm time";
  }
  if (dates.length === 0 && !weeks?.length && !weeksOf?.length) {
    return "needs from, to, weeks or weeksOf";
  }
  if (weeksOf?.some((date) => !YEARLY_DATE.test(date) || !isValid(parseISO(`2028-${date}`)))) {
    return "weeksOf must be MM-DD days";
  }
  if (dates.some((date) => !ONE_OFF_DATE.test(date) && !YEARLY_DATE.test(date))) {
    return "dates must be YYYY-MM-DD or MM-DD";
  }
  if (dates.some((date) => YEARLY_DATE.test(date)) && !(from && to && from.length === to.length)) {
    return "a yearly MM-DD window needs both from and to as MM-DD";
  }
  if (from && to && ONE_OFF_DATE.test(from) && from > to) {
    return "from is after to";
  }
  if (weeks?.some((week) => !Number.isInteger(week) || week < 1 || week > 53)) {
    return "weeks must be ISO week numbers from 1 to 53";
  }
  return undefined;
}

/** Whether something with this availability is on sale on a `yyyy-MM-dd` London day. */
export function isAvailableOnDay(availability: ItemAvailability, day: string) {
  const { from, to, weeks, weeksOf } = availability;

  if (from && to && YEARLY_DATE.test(from)) {
    const monthDay = day.slice(5);
    const inWindow =
      from <= to ? monthDay >= from && monthDay <= to : monthDay >= from || monthDay <= to;
    if (!inWindow) {
      return false;
    }
  } else {
    if (from && day < from) {
      return false;
    }
    if (to && day > to) {
      return false;
    }
  }

  if (weeks?.length && !weeks.includes(getISOWeek(parseISO(day)))) {
    return false;
  }

  if (weeksOf?.length && !weeksOf.some((date) => isInWeekOf(date, day))) {
    return false;
  }

  return true;
}

/**
 * Whether `day` (`yyyy-MM-dd`) is in the Monday-to-Sunday week that the
 * yearly `monthDay` (`MM-DD`) falls in, this year or either side of New Year.
 * A 29 February only comes round in leap years.
 */
function isInWeekOf(monthDay: string, day: string) {
  const week = format(startOfISOWeek(parseISO(day)), "yyyy-MM-dd");
  const year = Number(day.slice(0, 4));

  return [year - 1, year, year + 1].some((candidate) => {
    const date = parseISO(`${candidate}-${monthDay}`);
    return (
      isValid(date) &&
      format(date, "MM-dd") === monthDay &&
      format(startOfISOWeek(date), "yyyy-MM-dd") === week
    );
  });
}

/** Whether something with this availability is on sale at `now`. */
function isAvailableAt(availability: ItemAvailability, now: Date) {
  const today = londonDay(now);
  const { from, fromTime } = availability;

  if (fromTime && today === from && londonTime(now) < fromTime) {
    return false;
  }
  return isAvailableOnDay(availability, today);
}

/** Whether the item is on sale right now. */
export function isItemAvailable(item: ShopItem, now = new Date()) {
  return !item.availability || isAvailableAt(item.availability, now);
}

/**
 * Whether the item went on sale in the last NEW_ITEM_DAYS days. Items without
 * an availability have always been on sale, so they're never new; seasonal
 * ones are new again each time they come back.
 */
export function isNewItem(item: ShopItem, now = new Date()) {
  const { availability } = item;
  const today = londonDay(now);

  if (!availability || !isAvailableAt(availability, now)) {
    return false;
  }

  for (let daysAgo = 1; daysAgo <= NEW_ITEM_DAYS; daysAgo++) {
    if (!isAvailableOnDay(availability, shiftDay(today, -daysAgo))) {
      return true;
    }
  }
  return false;
}

/**
 * The last London day (`yyyy-MM-dd`) the item is on sale, if it's on sale now
 * and stops within the next couple of months.
 */
export function getAvailableUntil(item: ShopItem, now = new Date()) {
  const { availability } = item;
  const today = londonDay(now);

  if (!availability || !isAvailableAt(availability, now)) {
    return undefined;
  }

  for (let daysAhead = 1; daysAhead <= UNTIL_LOOKAHEAD_DAYS; daysAhead++) {
    if (!isAvailableOnDay(availability, shiftDay(today, daysAhead))) {
      return shiftDay(today, daysAhead - 1);
    }
  }
  return undefined;
}

/** The event the item is being sold for right now, if any. */
export function getCurrentEvent(item: ShopItem, now = new Date()) {
  return item.availability?.event && isItemAvailable(item, now)
    ? item.availability.event
    : undefined;
}
